import { Plus } from 'lucide-react';
import { TLBaseShape, BaseBoxShapeUtil, HTMLContainer, RecordProps, T, stopEventPropagation, useDialogs } from 'tldraw';
import { v4 as uuidv4 } from 'uuid';

import { addNode, NodeData } from '../../timelineDrawingHelpers';
import { NodeCreationModal } from '../Modals/NodeCreationModal';

type IAddNewNodeButton = TLBaseShape<
  'add-new-node-button',
  {
    w: number;
    h: number;
  }
>;

export class AddNewNodeButton extends BaseBoxShapeUtil<IAddNewNodeButton> {
  static override type = 'add-new-node-button' as const;
  static override props: RecordProps<IAddNewNodeButton> = {
    w: T.number,
    h: T.number,
  };

  getDefaultProps(): IAddNewNodeButton['props'] {
    return {
      w: 40,
      h: 40,
    };
  }

  component(shape: IAddNewNodeButton) {
    const { addDialog } = useDialogs();

    const handleAddNewNode = async () => {
      // Open TLDraw dialog
      addDialog({
        component: ({ onClose }) => (
          <NodeCreationModal onClose={onClose} onSave={(formData) => handleModalSave(formData, onClose)} />
        ),
        onClose() {
          // Dialog closed without saving then do nothing
        },
      });
    };

    const handleModalSave = (
      nodeData: { dateText: string; description: string; documentNames: string; docId: string },
      onClose: () => void,
    ) => {
      const newNodeId = uuidv4();

      const newNodeData: NodeData = {
        nodeId: newNodeId,
        date: '',
        dateText: nodeData.dateText,
        description: nodeData.description,
        documentNames: nodeData.documentNames,
        docId: nodeData.docId,
      };

      // Use the new addNode function to add at the end
      addNode(this.editor, newNodeData, 'end');

      // Close the dialog
      onClose();
    };

    return (
      <HTMLContainer id={shape.id} style={{ pointerEvents: 'all' }}>
        <button
          onPointerDown={stopEventPropagation}
          onClick={handleAddNewNode}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600"
        >
          <Plus size={24} />
        </button>
      </HTMLContainer>
    );
  }

  indicator(shape: IAddNewNodeButton) {
    return <rect width={shape.props.w} height={shape.props.h} />;
  }
}
